import type { Row } from "@rocicorp/zero";
import {
  crossResourceRule,
  type GrantRow,
  requiredSupplierPermissions,
} from "../../shared/rbac/crossResourceAvailability";
import type { Permission } from "../../shared/rbac/permissions";
import { grantHasExactPermission } from "../../shared/rbac/roles";
import type { queries } from "../../shared/queries";
import type { DisconnectImpact } from "../components/DisconnectWarning";
import type { WorkflowPreview } from "../components/WorkflowPreviewList";

type ConnectorRow = Row<typeof queries.groupConnectorLiveUsageByConnector>;
type GitRow = Row<typeof queries.groupConnectorGitRemoteUsage>;
type SpaceRow = Row<typeof queries.spaces>;

// Client mirror of the server's group-removal usage drop for the space rule:
// per connector the group grants, a removed user loses the permissions they
// keep through no other route, and every shared space that still requires them
// for that user gets its usages of the connector dropped. Best-effort (e.g.
// unaware of tenant-admin allow_all); the server drop stays authoritative.
export function groupRemovalDisconnects({
  groupId,
  removedUserIds,
  connectorRows,
  gitRows,
  spaces,
  userGroupMemberships,
}: {
  groupId: string;
  removedUserIds: readonly string[];
  connectorRows: readonly ConnectorRow[];
  gitRows: readonly GitRow[];
  spaces: readonly SpaceRow[];
  userGroupMemberships: ReadonlyMap<string, readonly string[]>;
}): DisconnectImpact {
  const permissions = [
    ...new Set(
      crossResourceRule("space_uses_connected_apps").tiers.flatMap(
        (tier) => tier.supplierPermissions
      )
    ),
  ];
  const sharedSpaces = spaces.filter((s) => s.mode !== "personal");

  const connectorGrants = new Map<string, readonly GrantRow[]>();
  for (const app of connectorRows) {
    connectorGrants.set(app.id, app.roleGrantsAsScope);
  }
  for (const row of gitRows) {
    if (!connectorGrants.has(row.connectedAppId) && row.connectedApp)
      connectorGrants.set(
        row.connectedAppId,
        row.connectedApp.roleGrantsAsScope
      );
  }

  const affectedSpacesByConnector = new Map<string, Set<string>>();
  for (const [connectedAppId, grants] of connectorGrants) {
    const affected = new Set<string>();
    for (const userId of removedUserIds) {
      const otherGroups = new Set(
        (userGroupMemberships.get(userId) ?? []).filter((g) => g !== groupId)
      );
      const reaches = (g: GrantRow) =>
        g.userId === userId ||
        (g.userGroupId != null && otherGroups.has(g.userGroupId));
      const lost = permissions.filter(
        (p) =>
          grants.some(
            (g) => g.userGroupId === groupId && grantHasExactPermission(g, p)
          ) && !grants.some((g) => reaches(g) && grantHasExactPermission(g, p))
      );
      if (lost.length === 0) continue;
      for (const space of sharedSpaces) {
        if (affected.has(space.id)) continue;
        if (
          space.roleGrantsAsScope.some(
            (g) =>
              reaches(g) &&
              requiredSupplierPermissions("space_uses_connected_apps", g).some(
                (p: Permission) => lost.includes(p)
              )
          )
        )
          affected.add(space.id);
      }
    }
    if (affected.size > 0)
      affectedSpacesByConnector.set(connectedAppId, affected);
  }

  const workflows = new Map<string, WorkflowPreview>();
  for (const app of connectorRows) {
    const affected = affectedSpacesByConnector.get(app.id);
    if (!affected) continue;
    for (const usage of app.usages) {
      const branch = usage.commit?.branch;
      const workflow = branch?.workflow;
      if (!branch || !workflow) continue;
      if (branch.headCommitId !== usage.refId || workflow.deletedAt != null)
        continue;
      if (!affected.has(workflow.spaceId)) continue;
      const existing = workflows.get(workflow.id);
      if (existing) {
        if (branch.name === "main") existing.draft = false;
      } else {
        workflows.set(workflow.id, {
          id: workflow.id,
          name: workflow.name,
          badgeSeed: workflow.badgeSeed,
          spaceId: workflow.spaceId,
          draft: branch.name !== "main",
        });
      }
    }
  }

  const gitSpaceNames = new Set<string>();
  for (const row of gitRows) {
    if (!affectedSpacesByConnector.get(row.connectedAppId)?.has(row.refId))
      continue;
    if (row.space?.name) gitSpaceNames.add(row.space.name);
  }

  return {
    workflows: [...workflows.values()],
    gitSpaceNames: [...gitSpaceNames],
  };
}
